import { useState, useEffect } from 'react';
import { 
  Calendar, Download, DollarSign, FileText, BarChart3, 
  PieChart as PieChartIcon, ChevronLeft, ChevronRight, RefreshCw
} from 'lucide-react';
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../utils/api';
import { CHART_COLORS, formatCurrency } from '../utils/helpers';
import YearMonthPicker from '../components/YearMonthPicker';
import { useSearchParams } from 'react-router-dom';

export default function ExpenseHistory() {
  const [searchParams] = useSearchParams();
  const requestedCustomerId = searchParams.get('customer_id');
  // 客戶選擇
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  
  // 檢視模式: 'monthly' 或 'range'
  const [viewMode, setViewMode] = useState('monthly');
  
  // 日期範圍
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // 數據
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [expenseItems, setExpenseItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  
  // 統計數據
  const [statistics, setStatistics] = useState({
    total: 0,
    count: 0,
    reportCount: 0,
    categories: [],
    trend: []
  });
  
  // UI 狀態
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchCustomers();
  }, [requestedCustomerId]);

  useEffect(() => {
    if (selectedCustomer) {
      if (viewMode === 'monthly') {
        loadReportsForMonth();
      }
      // 區間模式下不自動載入,等待使用者按下查詢按鈕
    }
  }, [selectedCustomer, currentMonth, viewMode]);

  useEffect(() => {
    if (selectedReport && selectedReport.id) {
      loadExpenseItems();
    }
  }, [selectedReport]);

  useEffect(() => {
    filterAndSortItems();
  }, [expenseItems]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?status=active');
      console.log('客戶 API 回應:', response.data);
      
      // 處理不同的回應格式
      let customerData = [];
      if (Array.isArray(response.data)) {
        customerData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        customerData = response.data.data;
      } else if (response.data.customers && Array.isArray(response.data.customers)) {
        customerData = response.data.customers;
      }
      
      console.log('解析後的客戶數據:', customerData);
      setCustomers(customerData);
      
      if (customerData.length > 0) {
        const requestedCustomer = requestedCustomerId
          ? customerData.find((customer) => String(customer.id) === requestedCustomerId)
          : null;
        const initialCustomer = requestedCustomer || customerData[0];

        setSelectedCustomer(initialCustomer.id);
        console.log('已選擇客戶:', initialCustomer.id, initialCustomer.company_name);
      } else {
        setMessage({ type: 'info', text: '目前沒有客戶資料,請先新增客戶' });
      }
    } catch (error) {
      console.error('獲取客戶列表失敗:', error);
      setMessage({ type: 'error', text: `無法載入客戶列表: ${error.message}` });
    }
  };

  const loadReportsForMonth = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/expense/customer/${selectedCustomer}/reports`
      );
      const reportList = response.data.reports || response.data || [];
      console.log('所有報表:', reportList);
      
      // 篩選當月報表 - 從檔案名稱解析帳期
      const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      const monthReports = reportList.filter(report => {
        // 嘗試從檔案名稱解析日期 (例如: AzurePlanDailyBilling-20241201-20241231.xlsx)
        const dateMatch = report.file_name.match(/(\d{8})-(\d{8})/);
        if (dateMatch) {
          // 解析開始和結束日期
          const startStr = dateMatch[1]; // 20241201
          const endStr = dateMatch[2];   // 20241231
          
          const reportStartDate = new Date(
            parseInt(startStr.substring(0, 4)),  // 年
            parseInt(startStr.substring(4, 6)) - 1,  // 月 (0-based)
            parseInt(startStr.substring(6, 8))   // 日
          );
          
          const reportEndDate = new Date(
            parseInt(endStr.substring(0, 4)),
            parseInt(endStr.substring(4, 6)) - 1,
            parseInt(endStr.substring(6, 8))
          );
          
          console.log(`報表 ${report.file_name} 帳期: ${reportStartDate.toLocaleDateString()} - ${reportEndDate.toLocaleDateString()}`);
          console.log(`當前月份範圍: ${monthStart.toLocaleDateString()} - ${monthEnd.toLocaleDateString()}`);
          
          // 檢查報表的帳期是否與選擇的月份重疊
          return (reportStartDate <= monthEnd && reportEndDate >= monthStart);
        }
        
        // 如果無法從檔案名稱解析,則使用上傳日期作為後備方案
        const uploadDate = new Date(report.uploaded_at || report.created_at);
        return uploadDate >= monthStart && uploadDate <= monthEnd;
      });
      
      console.log('篩選後的報表:', monthReports);
      setReports(monthReports);
      
      // 載入當月所有報表的彙總統計
      if (monthReports.length > 0) {
        // 計算當月日期範圍
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const startDateFull = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(year, month + 1, 0).getDate();
        const endDateFull = `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`;
        
        try {
          const statsResponse = await api.get(
            `/expense/customer/${selectedCustomer}/range-statistics`,
            {
              params: {
                start_date: startDateFull,
                end_date: endDateFull
              }
            }
          );
          
          const data = statsResponse.data;
          console.log('當月彙總統計:', data);
          
          // 更新統計數據（彙總當月所有報表）
          setStatistics({
            total: data.total_amount || 0,
            totalDiscounted: data.total_discounted_amount || data.total_amount || 0,
            totalTax: data.total_tax_amount || 0,
            totalWithTax: data.total_amount_with_tax || data.total_discounted_amount || data.total_amount || 0,
            hasDiscount: data.has_discount || false,
            hasTax: data.has_tax || false,
            count: data.total_count || 0,
            reportCount: data.report_count || monthReports.length,
            categories: data.categories || [],
            trend: data.monthly_trend || []
          });
          
          // 設定當月所有費用項目
          setExpenseItems(data.items || []);
          setFilteredItems(data.items || []);
          
          // 不再自動選擇報表，因為我們已經顯示彙總數據
          setSelectedReport(null);
          setMessage({ type: 'success', text: `已載入 ${data.report_count || monthReports.length} 份報表,共 ${data.total_count || 0} 筆費用` });
        } catch (statsError) {
          console.error('載入月度統計失敗:', statsError);
          // 失敗時退回到選擇第一個報表
          setSelectedReport(monthReports[0]);
          setMessage({ type: '', text: '' });
        }
      } else {
        // 清空所有數據
        setSelectedReport(null);
        setExpenseItems([]);
        setFilteredItems([]);
        setStatistics({
          total: 0,
          count: 0,
          reportCount: 0,
          categories: [],
          trend: []
        });
        setMessage({ type: 'info', text: '本月暫無費用記錄' });
      }
    } catch (error) {
      console.error('載入報表失敗:', error);
      setMessage({ type: 'error', text: '無法載入報表數據' });
    } finally {
      setLoading(false);
    }
  };

  const loadExpenseItems = async () => {
    if (!selectedReport || !selectedReport.id) {
      console.log('沒有選擇報表或報表沒有 ID');
      return;
    }
    
    setLoading(true);
    console.log('載入費用明細,報表 ID:', selectedReport.id);
    try {
      const response = await api.get(
        `/expense/${selectedReport.id}/items`
      );
      const items = response.data.items || [];
      console.log('載入的費用明細項目:', items.length, '項');
      setExpenseItems(items);
      calculateStatistics(items);
      if (items.length > 0) {
        setMessage({ type: '', text: '' }); // 清除訊息
      }
    } catch (error) {
      console.error('載入費用明細失敗:', error);
      setMessage({ type: 'error', text: '無法載入費用明細' });
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (items) => {
    const total = items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    // 按類別統計
    const categoryMap = {};
    items.forEach(item => {
      const category = item.category || '未分類';
      if (!categoryMap[category]) {
        categoryMap[category] = 0;
      }
      categoryMap[category] += parseFloat(item.amount || 0);
    });
    
    const categories = Object.entries(categoryMap).map(([name, value]) => ({
      name,
      value: parseFloat(value.toFixed(2))
    }));
    
    setStatistics({
      total,
      count: items.length,
      categories,
      trend: [] // TODO: 實現趨勢分析
    });
  };

  const filterAndSortItems = () => {
    // 直接使用所有項目,不進行篩選和排序
    setFilteredItems(expenseItems);
  };

  const changeMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setCurrentMonth(newMonth);
  };

  const handleYearChange = (year) => {
    const newMonth = new Date(currentMonth);
    newMonth.setFullYear(parseInt(year));
    setCurrentMonth(newMonth);
  };

  const handleMonthChange = (month) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(parseInt(month));
    setCurrentMonth(newMonth);
  };

  // 生成年份選項（固定為 2020 至 2040 年）
  const generateYearOptions = () => {
    const years = [];
    for (let year = 2020; year <= 2040; year++) {
      years.push(year);
    }
    return years;
  };

  // 月份選項
  const months = [
    { value: 0, label: '1月' },
    { value: 1, label: '2月' },
    { value: 2, label: '3月' },
    { value: 3, label: '4月' },
    { value: 4, label: '5月' },
    { value: 5, label: '6月' },
    { value: 6, label: '7月' },
    { value: 7, label: '8月' },
    { value: 8, label: '9月' },
    { value: 9, label: '10月' },
    { value: 10, label: '11月' },
    { value: 11, label: '12月' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(amount || 0));
  };

  // 費用項目明細用 - 保留小數
  const formatCurrencyDecimal = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const exportToExcel = async () => {
    if (!selectedReport) return;
    
    try {
      const response = await api.post(
        `/expense/${selectedReport.id}/generate-excel`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `費用明細_${formatDate(new Date())}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setMessage({ type: 'success', text: 'Excel 報表已下載' });
    } catch (error) {
      console.error('匯出失敗:', error);
      setMessage({ type: 'error', text: '匯出失敗' });
    }
  };

  // PDF 匯出功能已移除

  const getCategoryColor = (category) => {
    const index = statistics.categories.findIndex(c => c.name === category);
    return CHART_COLORS[index % CHART_COLORS.length];
  };

  // 載入區間統計資料
  const loadRangeStatistics = async () => {
    if (!startDate || !endDate) {
      setMessage({ type: 'error', text: '請選擇開始月份和結束月份' });
      return;
    }

    setLoading(true);
    try {
      // YYYY-MM-DD → 直接使用；YYYY-MM → 展開為整月起訖
      const startDateFull = startDate.length === 10 ? startDate : `${startDate}-01`;
      const endDateFull = (() => {
        if (endDate.length === 10) return endDate;
        const [y, m] = endDate.split('-');
        return `${endDate}-${new Date(parseInt(y), parseInt(m), 0).getDate()}`;
      })();

      const response = await api.get(
        `/expense/customer/${selectedCustomer}/range-statistics`,
        {
          params: {
            start_date: startDateFull,
            end_date: endDateFull
          }
        }
      );

      const data = response.data;
      console.log('區間統計資料:', data);

      // 更新統計數據
      setStatistics({
        total: data.total_amount || 0,
        totalDiscounted: data.total_discounted_amount || data.total_amount || 0,
        totalTax: data.total_tax_amount || 0,
        totalWithTax: data.total_amount_with_tax || data.total_discounted_amount || data.total_amount || 0,
        hasDiscount: data.has_discount || false,
        hasTax: data.has_tax || false,
        count: data.total_count || 0,
        reportCount: data.report_count || 0,
        categories: data.categories || [],
        trend: data.monthly_trend || []
      });

      // 更新費用項目
      setExpenseItems(data.items || []);
      setFilteredItems(data.items || []);

      // 清空報表選擇(因為是區間查詢)
      setReports([]);
      setSelectedReport(null);

      if (data.total_count === 0) {
        setMessage({ type: 'info', text: '此區間內暫無費用記錄' });
      } else {
        setMessage({ type: 'success', text: `已載入 ${data.report_count} 份報表,共 ${data.total_count} 筆費用` });
      }
    } catch (error) {
      console.error('載入區間統計失敗:', error);
      setMessage({ type: 'error', text: '無法載入區間統計數據' });
    } finally {
      setLoading(false);
    }
  };

  // 快速選擇月份區間
  const setQuickRange = (rangeType) => {
    const today = new Date();
    let startYear, startMonth, endYear, endMonth;

    switch (rangeType) {
      case 'thisMonth':
        startYear = today.getFullYear();
        startMonth = today.getMonth();
        endYear = today.getFullYear();
        endMonth = today.getMonth();
        break;
      case 'lastMonth':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1);
        startYear = lastMonth.getFullYear();
        startMonth = lastMonth.getMonth();
        endYear = lastMonth.getFullYear();
        endMonth = lastMonth.getMonth();
        break;
      case 'thisQuarter':
        const currentQuarter = Math.floor(today.getMonth() / 3);
        startYear = today.getFullYear();
        startMonth = currentQuarter * 3;
        endYear = today.getFullYear();
        endMonth = currentQuarter * 3 + 2;
        break;
      case 'lastQuarter':
        const lastQuarterNum = Math.floor(today.getMonth() / 3) - 1;
        if (lastQuarterNum < 0) {
          startYear = today.getFullYear() - 1;
          startMonth = 9; // Q4 starts at October (month 9)
          endYear = today.getFullYear() - 1;
          endMonth = 11; // Q4 ends at December (month 11)
        } else {
          startYear = today.getFullYear();
          startMonth = lastQuarterNum * 3;
          endYear = today.getFullYear();
          endMonth = lastQuarterNum * 3 + 2;
        }
        break;
      case 'thisYear':
        startYear = today.getFullYear();
        startMonth = 0;
        endYear = today.getFullYear();
        endMonth = 11;
        break;
      case 'lastYear':
        startYear = today.getFullYear() - 1;
        startMonth = 0;
        endYear = today.getFullYear() - 1;
        endMonth = 11;
        break;
      default:
        return;
    }

    // 格式化為 YYYY-MM
    setStartDate(`${startYear}-${String(startMonth + 1).padStart(2, '0')}`);
    setEndDate(`${endYear}-${String(endMonth + 1).padStart(2, '0')}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* 頁面標題與客戶選擇 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">💳 費用明細帳單</h1>
              <p className="text-gray-500 mt-1">查看歷史費用記錄與分析</p>
            </div>
            
            {/* 客戶選擇 */}
            <div className="flex items-center space-x-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                選擇客戶:
              </label>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className="min-w-[200px] px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 font-medium cursor-pointer hover:border-blue-400 transition-colors"
              >
                {customers.length === 0 ? (
                  <option value="">載入中...</option>
                ) : (
                  customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.customer_name || customer.name} - {customer.company || '個人'} - {customer.name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>

        {/* 訊息提示 */}
        {message.text && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800' :
            message.type === 'error' ? 'bg-red-50 text-red-800' :
            'bg-blue-50 text-blue-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* 檢視模式切換 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">檢視模式</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => setViewMode('monthly')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'monthly'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📅 月度檢視
              </button>
              <button
                onClick={() => setViewMode('range')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'range'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📊 區間統計
              </button>
            </div>
          </div>
        </div>

        {/* 月份選擇器 - 只在月度檢視時顯示 */}
        {viewMode === 'monthly' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* 左側箭頭 */}
            <button
              onClick={() => changeMonth('prev')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="上一個月"
            >
              <ChevronLeft className="w-6 h-6 text-gray-600" />
            </button>
            
            {/* 中間年份和月份選擇器 */}
            <div className="flex items-center space-x-4">
              <Calendar className="w-6 h-6 text-blue-500" />
              
              {/* 年份下拉選單 */}
              <select
                value={currentMonth.getFullYear()}
                onChange={(e) => handleYearChange(e.target.value)}
                className="px-4 py-2 text-xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 cursor-pointer hover:border-blue-400 transition-colors"
              >
                {generateYearOptions().map(year => (
                  <option key={year} value={year}>{year} 年</option>
                ))}
              </select>
              
              {/* 月份下拉選單 */}
              <select
                value={currentMonth.getMonth()}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="px-4 py-2 text-xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 cursor-pointer hover:border-blue-400 transition-colors"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            
            {/* 右側箭頭 */}
            <button
              onClick={() => changeMonth('next')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="下一個月"
            >
              <ChevronRight className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>
        )}

        {/* 區間選擇器 - 只在區間統計時顯示 */}
        {viewMode === 'range' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="space-y-4">
            {/* 快速選擇按鈕 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">快速選擇</label>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setQuickRange('thisMonth')} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                  本月
                </button>
                <button onClick={() => setQuickRange('lastMonth')} className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                  上月
                </button>
                <button onClick={() => setQuickRange('thisQuarter')} className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                  本季
                </button>
                <button onClick={() => setQuickRange('lastQuarter')} className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
                  上季
                </button>
                <button onClick={() => setQuickRange('thisYear')} className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                  今年
                </button>
                <button onClick={() => setQuickRange('lastYear')} className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
                  去年
                </button>
              </div>
            </div>

            {/* 自訂月份範圍 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <YearMonthPicker
                label="開始月份"
                value={startDate}
                onChange={setStartDate}
                mode="month"
                showDayPicker
                className="border-2"
              />
              <YearMonthPicker
                label="結束月份"
                value={endDate}
                onChange={setEndDate}
                mode="month"
                showDayPicker
                className="border-2"
              />
              <div className="flex items-end">
                <button
                  onClick={loadRangeStatistics}
                  disabled={!startDate || !endDate || loading}
                  className="w-full px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>查詢中...</span>
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-4 h-4" />
                      <span>查詢統計</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500">{viewMode === 'monthly' ? '本月總支出' : '區間總支出'}</p>
              <DollarSign className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(statistics.hasTax ? statistics.totalWithTax : (statistics.hasDiscount ? statistics.totalDiscounted : statistics.total))}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              共 {statistics.count} 筆交易
              {statistics.hasDiscount && (
                <span className="ml-2 text-purple-600">
                  (原價 {formatCurrency(statistics.total)})
                </span>
              )}
              {statistics.hasTax && (
                <span className="ml-2 text-orange-600">
                  (含稅 +{formatCurrency(statistics.totalTax)})
                </span>
              )}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500">報表數量</p>
              <FileText className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {statistics.reportCount || reports.length || 0}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {viewMode === 'monthly' ? '本月上傳報表數' : '包含的報表數'}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-500">主要類別</p>
              <PieChartIcon className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {statistics.categories[0]?.name || '無數據'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              {statistics.categories[0] ? formatCurrency(statistics.categories[0].value) : '---'}
            </p>
          </div>
        </div>

        {/* 圖表區域 */}
        {statistics.categories.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">分類細項分析</h3>
              <div className="flex space-x-2">
                <button
                  onClick={exportToExcel}
                  disabled={viewMode === 'range' || !selectedReport}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center space-x-2"
                  title={viewMode === 'range' ? '區間模式下不支援匯出' : ''}
                >
                  <Download className="w-4 h-4" />
                  <span>匯出 Excel</span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">類別分佈</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statistics.categories}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => {
                        const percentage = (percent * 100);
                        return percentage >= 3 ? `${name} (${percentage.toFixed(0)}%)` : '';
                      }}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statistics.categories.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        formatCurrency(value),
                        name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">類別金額</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statistics.categories}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="value" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* 月度趨勢圖 - 只在區間模式下顯示 */}
            {viewMode === 'range' && statistics.trend && statistics.trend.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-semibold mb-4">月度趨勢</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={statistics.trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), '金額']}
                      labelFormatter={(label) => `月份: ${label}`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="amount" 
                      name="月度費用"
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {/* 費用明細列表 */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <h3 className="text-lg font-semibold px-6 py-4 border-b">費用項目明細</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">類別</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">服務名稱</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">筆數</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">金額</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
                      <p>載入中...</p>
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>暫無費用記錄</p>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item, index) => (
                    <tr key={index} className={`hover:bg-gray-50 ${item.is_manual ? 'bg-indigo-50/50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          item.is_manual ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {item.category || '未分類'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.service_name || item.merchant || '未知服務'}
                        {item.is_manual && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded">手動</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                          {item.quantity ? `${Math.floor(item.quantity)} 項` : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {item.description ? item.description.replace(/^[^(]+\(\d+\s*[項筆服務?]+\)\s*-?\s*/, '') || item.service_name || item.merchant : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrencyDecimal(item.amount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 分頁資訊 */}
          {filteredItems.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
              <p className="text-sm text-gray-700">
                顯示 <span className="font-medium">{filteredItems.length}</span> 筆記錄
              </p>
              <p className="text-sm text-gray-700">
                總金額: <span className="font-bold text-blue-600">
                  {formatCurrency(filteredItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0))}
                </span>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
